// TODO: If you make edits to this file you must deal with these ESLint rules
/*eslint-disable react-hooks/exhaustive-deps */

import React, { useCallback, useRef, useState } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';

import { EstimateActions, EstimateType } from '@zego/enums';
import useAttribution from '@zego/hooks/useAttribution';
import useLogout from '@zego/hooks/useLogout';
import useStableCallback from '@zego/hooks/useStableCallback';
import sentry from '@zego/sentry';
import { useTracking } from '@zego/tracking';
import { withTrackingScope } from '@zego/tracking';

import QuickEstimatesAuthModal from '../../forms/QuickEstimatesAuthModal';
import { setPromotionRefUrl } from '../../utils';
import useCarEstimateForm from '../useCarEstimateForm';
import CarQuickEstimatesFormCore from './CarQuickEstimatesFormCore';

function CarQuickEstimatesForm({ viewer, ...props }) {
  const logout = useLogout();
  const [error, setError] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const attribution = useAttribution();
  const { trackEvent } = useTracking();

  const [submittingKind, setSubmittingKind] = useState(null);

  const deferredSubmit = useRef(null);

  const openModal = useCallback(() => setModalIsOpen(true), []);
  const closeModal = useCallback(() => setModalIsOpen(false), []);

  const name = viewer?.currentUser?.fullName;
  const isLoggedIn = !!name;

  const _onSubmit = useStableCallback(
    useCarEstimateForm(null, values => ({
      action: EstimateActions.UPDATE_DATA,
      data: {
        person: {
          dateOfBirth: values.bday,
          postcode: values.postcode.trim(),
        },
        vehicle: {
          registrationNumber: values.registration,
        },
      },
      attribution,
    })),
    [],
  );

  const wrappedSubmit = useCallback(
    (values, onComplete) => {
      setPromotionRefUrl(props.locationName);
      trackEvent('Website: Quick Estimates - Short form submitted');

      _onSubmit(values, {
        onError: err => {
          if (err) {
            sentry.captureException(err);
          }

          setModalIsOpen(false);
          setError('Something went wrong');
          onComplete();
          setSubmittingKind(null);
        },
      });
    },
    [_onSubmit, trackEvent],
  );

  const _anonymousOnSubmit = useCallback(
    (values, cb) => {
      setSubmittingKind('anonymous');
      wrappedSubmit(values, cb);
    },
    [wrappedSubmit],
  );

  const _authenticatedOnSubmit = useCallback(
    (values, cb) => {
      deferredSubmit.current = () => {
        wrappedSubmit(values, cb);
      };
      openModal();
    },
    [openModal, wrappedSubmit],
  );

  return (
    <>
      <CarQuickEstimatesFormCore
        {...props}
        error={error}
        submitting={submittingKind === 'anonymous'}
        onSubmit={isLoggedIn ? _authenticatedOnSubmit : _anonymousOnSubmit}
      />
      <QuickEstimatesAuthModal
        name={name}
        isOpen={modalIsOpen}
        onClose={closeModal}
        onAccept={() => {
          setSubmittingKind('continue');
          deferredSubmit.current();
        }}
        onReject={async () => {
          setSubmittingKind('logout');
          await logout();
          deferredSubmit.current();
        }}
        isAccepting={submittingKind === 'continue'}
        isRejecting={submittingKind === 'logout'}
      />
    </>
  );
}

const CarQuickEstimatesFormContainer = createFragmentContainer(
  CarQuickEstimatesForm,
  {
    viewer: graphql`
      fragment CarQuickEstimatesForm_viewer on Viewer {
        currentUser {
          fullName
        }
      }
    `,
  },
);

export default withTrackingScope({
  estimateType: EstimateType.GB_CAR,
  featureName: 'Car Quick Estimates Widget',
})(CarQuickEstimatesFormContainer);
